import { TEST_ID_SIMILAR_PLAYER } from "e2e/testId";
import { Circle, CircleCheck } from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
} from "react-native";
import { RenderItem } from "react-native-draggable-flatlist";
import Animated, {
  FadeInLeft,
  FadeOutLeft,
  Layout,
  ReduceMotion,
} from "react-native-reanimated";

import CustomMultiSelectView from "./MultiSelectView";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Button } from "@/components/atoms/Button";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { useTheme } from "@/providers/ThemeProvider";
import { IFilterOption } from "@/stores/filterValues";

const AnimatedView = Platform.OS === "android" ? Box : Animated.View || Box;

/**
 * Props for the CustomMultiSelectContainer component
 * @template T - Type of the filter option extending IFilterOption
 */
interface Props<T extends IFilterOption> {
  title: string;
  loading: boolean;
  listSelected: T[];
  data: T[];
  refetching?: boolean;
  onSelect?: (value: T[]) => void;
  onReachEnd?: () => void;
  /**
   * Opt the picker into a type-to-filter search input pinned above the option
   * list. Defaults to off so existing consumers are unaffected. Filtering is
   * purely client-side over the already-loaded `data` and never mutates the
   * selection.
   */
  enableSearch?: boolean;
  /** Placeholder shown in the search input when `enableSearch` is true. */
  searchPlaceholder?: string;
  /**
   * Copy shown in the option slot when the caller's read FAILED, in place of
   * the default "No <title>" (SE-7580). Those are different claims: "No
   * Leagues" asserts the org has none, which a failed read never established.
   * Leave undefined whenever the read succeeded — including when it genuinely
   * returned nothing.
   */
  errorMessage?: string;
  /** Retries the caller's read. Rendered as a CTA beside `errorMessage`. */
  onRetry?: () => void;
  /**
   * Disclosure shown under the option list when the options ARE rendered but
   * their refresh failed (SE-7580). Without it a cached list reads as current,
   * which is the same swallowed-error problem as `errorMessage` guards against
   * one state over.
   */
  noticeMessage?: string;
}

const SELECTED_COLOR = "#0da6f2";
const ICON_SIZE = "xl";
/** Padding from bottom to trigger onReachEnd callback */
const SCROLL_END_THRESHOLD = 20;

/**
 * Container component for multi-select dropdown with animated selected items.
 * Handles selection logic, dropdown state, and item rendering.
 * @param root0 - Component props
 * @param root0.data - Array of available options to display in dropdown
 * @param root0.title - Title text displayed above the dropdown and in empty state
 * @param root0.loading - Whether initial data is loading (shows skeleton placeholder)
 * @param root0.onSelect - Callback invoked when items are selected or deselected with new selection array
 * @param root0.onReachEnd - Callback invoked when user scrolls to end of dropdown list (for pagination)
 * @param root0.listSelected - Array of currently selected items
 * @param root0.refetching - Whether additional data is being fetched (shows loading indicator in footer)
 * @param root0.enableSearch - Whether to render a type-to-filter search input above the option list
 * @param root0.searchPlaceholder - Placeholder for the search input when enableSearch is true
 * @param root0.errorMessage - Copy shown in the option slot when the read failed
 * @param root0.onRetry - Retries the caller's read, rendered beside errorMessage
 * @param root0.noticeMessage - Disclosure shown under a rendered-but-stale option list
 */
const CustomMultiSelectContainer = <T extends IFilterOption>({
  data,
  title,
  loading,
  onSelect,
  onReachEnd,
  listSelected,
  refetching,
  enableSearch,
  searchPlaceholder,
  errorMessage,
  onRetry,
  noticeMessage,
}: Props<T>) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const handleSearchTextChange = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  const filteredData = useMemo(() => {
    if (!enableSearch) {
      return data;
    }
    const query = searchText.trim().toLowerCase();
    if (query.length === 0) {
      return data;
    }
    return data.filter(item => item.name.toLowerCase().includes(query));
  }, [enableSearch, data, searchText]);

  const handleSelect = useCallback(
    (value: T) => {
      // Defer state update until after touch interaction completes
      // InteractionManager.runAfterInteractions(() => {
      const index = listSelected.findIndex(e => e.id === value.id);
      if (index > -1) {
        onSelect?.(listSelected.filter(i => i.id !== value.id));
      } else {
        onSelect?.([...listSelected, value]);
      }
      // });
    },
    [listSelected, onSelect]
  );

  const handleUnSelect = useCallback(
    (id: string) => {
      // Defer state update until after touch interaction completes
      // InteractionManager.runAfterInteractions(() => {
      onSelect?.(listSelected.filter(i => i.id !== id));
      // });
    },
    [listSelected, onSelect]
  );

  const handleToggleDropdown = useCallback(() => {
    setIsOpenDropdown(prev => !prev);
  }, []);

  const createUnselectHandler = useCallback(
    (id: string) => () => handleUnSelect(id),
    [handleUnSelect]
  );

  const createSelectHandler = useCallback(
    (item: T) => () => handleSelect(item),
    [handleSelect]
  );

  const renderListSelected = useCallback(
    (item: T, index: number) => {
      const Container = Platform.OS === "web" ? AnimatedView : Box;
      return (
        <Container
          key={item.id}
          {...(Platform.OS === "web"
            ? {
                layout: Layout.springify(),
                entering: FadeInLeft.delay(index * 100).reduceMotion(
                  ReduceMotion.System
                ),
                exiting: FadeOutLeft.reduceMotion(ReduceMotion.System),
              }
            : {})}
        >
          <Pressable
            key={item.id + index.toString()}
            onPress={createUnselectHandler(item.id)}
            className="max-w-full flex-row items-center gap-4 rounded-full bg-accent-secondary p-2 px-4"
            android_ripple={{
              color: "rgba(255, 255, 255, 0.3)",
              borderless: false,
            }}
          >
            <Text
              variant="unstyled"
              isTruncated
              className={`shrink font-medium text-title ${isDark ? "text-white" : "text-content-inverse"}`}
            >
              {item.name}
            </Text>
            <Text
              variant="unstyled"
              className={`font-medium text-title ${isDark ? "text-white" : "text-content-inverse"}`}
            >
              ⓧ
            </Text>
          </Pressable>
        </Container>
      );
    },
    [createUnselectHandler, isDark]
  );

  const renderDropdownItem = useCallback<RenderItem<T>>(
    ({ item }) => {
      const isSelected =
        listSelected.findIndex(selectTemp => selectTemp.id === item.id) !== -1;
      return (
        <Pressable
          testID={TEST_ID_SIMILAR_PLAYER.LEAGUES_DROPDOWN_ITEM}
          key={item?.id}
          onPress={createSelectHandler(item)}
          className="flex-row items-center p-3 pl-2"
          android_ripple={{
            color: "rgba(0, 0, 0, 0.1)",
            borderless: false,
          }}
        >
          <Box
            testID={
              isSelected
                ? TEST_ID_SIMILAR_PLAYER.LEAGUES_DROPDOWN_ITEM_SELECTED
                : TEST_ID_SIMILAR_PLAYER.LEAGUES_DROPDOWN_ITEM_UNSELECTED
            }
          >
            {isSelected ? (
              <Icon as={CircleCheck} size={ICON_SIZE} color={SELECTED_COLOR} />
            ) : (
              <Icon as={Circle} size={ICON_SIZE} color="gray" />
            )}
          </Box>
          <Text
            variant="unstyled"
            className={`${isSelected ? "pr-6" : ""} shrink pl-4 font-body text-title`}
          >
            {item.name}
          </Text>
        </Pressable>
      );
    },
    [createSelectHandler, listSelected]
  );

  // SE-7580: the "No <title>" copy is a claim about the DATA, so it renders
  // only when the caller reports a successful read. A failed read gets an
  // error plus a retry instead of being laundered into an authoritative empty.
  const renderEmpty = useCallback(
    () =>
      errorMessage ? (
        <Box accessibilityRole="alert" className="flex-1">
          <Stack className="flex-1 items-center justify-center gap-3 p-4">
            <Text variant="body" className="text-center text-content-muted">
              {errorMessage}
            </Text>
            {onRetry ? (
              <Button
                label="Try again"
                tone="secondary"
                variant="outline"
                size="sm"
                labelTone="secondary"
                labelVariant="body"
                onPress={onRetry}
              />
            ) : null}
          </Stack>
        </Box>
      ) : (
        <Stack className="flex-1 items-center justify-center p-4">
          <Text variant="display-sm" className="text-center">
            No {title}
          </Text>
        </Stack>
      ),
    [title, errorMessage, onRetry]
  );

  // SE-7580: the footer carries the staleness disclosure as well as the
  // paging spinner, so options whose refresh failed are shown WITH that fact
  // rather than passed off as current.
  const renderLoadingIndicator = useCallback(
    () => (
      <Stack className="items-center gap-2">
        {noticeMessage ? (
          <Box accessibilityRole="alert" className="w-full px-4 py-2">
            <Text
              variant="caption"
              className="text-center text-status-warning-strong"
            >
              {noticeMessage}
            </Text>
            {onRetry ? (
              <Stack className="items-center pt-2">
                <Button
                  label="Try again"
                  tone="secondary"
                  variant="outline"
                  size="sm"
                  labelTone="secondary"
                  labelVariant="caption"
                  onPress={onRetry}
                />
              </Stack>
            ) : null}
          </Box>
        ) : null}
        {refetching ? <Spinner color="white" /> : <Box />}
      </Stack>
    ),
    [refetching, noticeMessage, onRetry]
  );

  /**
   * Handles scroll events to detect when user is near the bottom of the list.
   * Triggers onReachEnd callback when scroll position is within threshold of bottom.
   */
  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { layoutMeasurement, contentOffset, contentSize } =
        event.nativeEvent;
      const isCloseToBottom =
        layoutMeasurement.height + contentOffset.y >=
        contentSize.height - SCROLL_END_THRESHOLD;
      if (isCloseToBottom && onReachEnd) {
        onReachEnd();
      }
    },
    [onReachEnd]
  );

  return (
    <CustomMultiSelectView
      data={filteredData}
      listSelected={listSelected}
      isOpenDropdown={isOpenDropdown}
      onToggleDropdown={handleToggleDropdown}
      renderListSelected={renderListSelected}
      renderDropdownItem={renderDropdownItem}
      renderEmpty={renderEmpty}
      onScroll={handleScroll}
      renderFooterComponent={renderLoadingIndicator}
      title={title}
      loading={loading}
      isDark={isDark}
      enableSearch={enableSearch}
      searchText={enableSearch ? searchText : undefined}
      searchPlaceholder={searchPlaceholder}
      onSearchTextChange={enableSearch ? handleSearchTextChange : undefined}
    />
  );
};

export default CustomMultiSelectContainer;
